/*----------------------------------
- DOMAINS RAIL ACCORDION
----------------------------------*/

/**
 * One filter of the public rail as the mock draws it (`dm-acc`, docs/mockups/website-domains.html `railHtml()`): an
 * icon tile, the label, the rule's summary ("Any"), the chevron; the body under it while open. A locked filter is the
 * dashed card with the lock in place of its icon, and its head is the plan's wall: it never opens.
 *
 * The test hooks are Browse's (`radar-rule-accordion-<id>`, `data-filter-disabled`), so the journeys that reached the
 * shipped rail reach this one unchanged.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { ComponentChildren } from 'preact';

import Icon from '@/client/components/ui/icon/Icon';
import type { SpriteIconName } from '@/client/components/ui/icon/spriteManifest.generated';
import { DOMAINS_RAIL_COPY } from '@/client/catalogs/landing/domains/pageCopy';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsRailAccordionProps = {
    filterId: string;
    label: string;
    icon: SpriteIconName;
    summary: string;
    open: boolean;
    locked: boolean;
    /** An unlocked head: opens or closes the body, never a wall (accordions expand freely, ADR-0035). */
    onToggle: () => void;
    /** A locked head: the plan's wall. */
    onLocked: () => void;
    children?: ComponentChildren;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const DomainsRailAccordion = ({ filterId, label, icon, summary, open, locked, onToggle, onLocked, children }: DomainsRailAccordionProps) => {
    const bodyId = `dm-acc-${filterId}`;
    const chevron = <Icon name="chevron-down" class="dm-acc__chev" size={14} strokeWidth={2} />;

    if (locked)
        return (
            <section class="dm-acc dm-acc--locked" data-testid={`radar-rule-accordion-${filterId}`} data-filter-disabled="true">
                <button type="button" class="dm-acc__head" onClick={onLocked}>
                    <span class="dm-acc__ic"><Icon name="lock" size={14} strokeWidth={2} /></span>
                    {label}
                    <span class="dm-acc__v">{summary}</span>
                    {/* The lock is a drawing; the word says it to a screen reader, after the visible text so the
                        name starts with what the button shows (WCAG 2.5.3). */}
                    <span class="ud-sr-only"> {DOMAINS_RAIL_COPY.locked}</span>
                    {chevron}
                </button>
            </section>
        );

    return (
        <section class="dm-acc" data-open={open ? '' : undefined} data-testid={`radar-rule-accordion-${filterId}`} data-filter-disabled="false">
            <button type="button" class="dm-acc__head" aria-expanded={open} aria-controls={bodyId} onClick={onToggle}>
                <span class="dm-acc__ic"><Icon name={icon} size={14} strokeWidth={2} /></span>
                {label}
                <span class="dm-acc__v">{summary}</span>
                {chevron}
            </button>
            <div class="dm-acc__body" id={bodyId} hidden={!open}>
                {children}
            </div>
        </section>
    );
};

export default DomainsRailAccordion;
